import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { getDocsByIdsChunked } from '../../utils/firestoreChunking';
import { formatMealDateTime } from '../../utils/dateFormat';
import './listingFeed.css';

const MEALS_PER_PAGE = 10;

const ListingFeed = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [meals, setMeals] = useState([]);
  const [bookedMealIds, setBookedMealIds] = useState(new Set());
  const [hostData, setHostData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchMeals = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      }

      const now = Timestamp.now();
      let mealsQuery = query(
        collection(db, 'meals'),
        where('status', '==', 'active'),
        where('bookingCutoffTime', '>=', now),
        orderBy('bookingCutoffTime', 'asc'),
        orderBy('dateTime', 'asc'),
        limit(MEALS_PER_PAGE)
      );

      if (isLoadMore && lastVisible) {
        mealsQuery = query(
          collection(db, 'meals'),
          where('status', '==', 'active'),
          where('bookingCutoffTime', '>=', now),
          orderBy('bookingCutoffTime', 'asc'),
          orderBy('dateTime', 'asc'),
          startAfter(lastVisible),
          limit(MEALS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(mealsQuery);
      const mealsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (querySnapshot.docs.length > 0) {
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }
      setHasMore(querySnapshot.docs.length === MEALS_PER_PAGE);

      if (isLoadMore) {
        setMeals(prev => [...prev, ...mealsData]);
      } else {
        setMeals(mealsData);
      }

      const uniqueHostIds = [...new Set(mealsData.map(m => m.hostId).filter(Boolean))];
      if (uniqueHostIds.length > 0) {
        const hostDocs = await getDocsByIdsChunked(collection(db, 'users'), uniqueHostIds);
        setHostData(prev => {
          const updated = { ...prev };
          hostDocs.forEach(d => { updated[d.id] = d.data(); });
          return updated;
        });
      }

      if (currentUser?.uid) {
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('guestId', '==', currentUser.uid),
          where('status', '==', 'confirmed')
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        const bookedIds = new Set(
          bookingsSnapshot.docs.map(doc => doc.data().mealId)
        );
        setBookedMealIds(bookedIds);
      }
    } catch (err) {
      console.error('Error fetching meals:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentUser, lastVisible]);

  useEffect(() => {
    fetchMeals();
  }, [currentUser]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchMeals(true);
    }
  };

  if (loading) {
    return (
      <div className="listing-feed-container">
        <p className="listing-feed-loading">Loading meals...</p>
      </div>
    );
  }

  return (
    <div className="listing-feed-container">
      <h2 className="listing-feed-title">Upcoming Meals</h2>
      {meals.length === 0 ? (
        <p className="listing-feed-empty">No active meals available.</p>
      ) : (
        <div className="listing-feed-timeline">
          {meals.map((meal) => {
            const { date, time, dayOfWeek } = formatMealDateTime(meal.dateTime);
            const host = meal.hostId ? hostData[meal.hostId] : null;
            const hostName = host
              ? [host.userProfile?.firstName, host.userProfile?.lastName].filter(Boolean).join(' ')
              : null;
            const hostRating = host?.hostRatings?.avgOverallRating;
            return (
              <div
                key={meal.id}
                className="timeline-item"
                onClick={() => navigate(`/meal-summary/${meal.id}`)}
              >
                <div className="timeline-card">
                  <div className="timeline-card-date">
                    <span className="timeline-day">{dayOfWeek}</span>
                    <span className="timeline-date-text">{date}</span>
                    <span className="timeline-time">{time}</span>
                  </div>
                  <div className="timeline-card-image">
                    {meal.photoUrl ? (
                      <img src={meal.photoUrl} alt={meal.title} />
                    ) : (
                      <div className="timeline-card-placeholder">
                        <span>No Photo</span>
                      </div>
                    )}
                  </div>
                  <div className="timeline-card-info">
                    <h3 className="timeline-card-title">{meal.title}</h3>
                    {meal.neighborhood && (
                      <p className="timeline-card-neighborhood">{meal.neighborhood}</p>
                    )}
                    {hostName && (
                      <p className="timeline-card-host">
                        <span className="timeline-card-host-label">Host: </span>
                        {hostName}
                        {hostRating != null && (
                          <span className="timeline-card-host-rating">&#9733; {hostRating.toFixed(1)}</span>
                        )}
                      </p>
                    )}
                    <p className="timeline-card-spots">
                      {meal.maxGuests - (meal.currentGuests || 0)} spots left
                    </p>
                  </div>
                  {bookedMealIds.has(meal.id) && (
                    <div className="timeline-card-booked">
                      <span className="booked-checkmark">✓</span>
                      <span className="booked-text">Booked</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <button
              className="load-more-button"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingFeed;
